// oxlint-disable typescript/no-explicit-any
import { isPlainObject } from 'lodash';

type AnyObject = Record<string | number | symbol, unknown>;
type Path = string;

export const objectKeys = <T extends object>(o: NonNullable<T>): (keyof T)[] => {
  return Object.keys(o) as (keyof T)[];
};

export const objectEntries = <T extends object>(o: NonNullable<T>): [keyof T, T[keyof T]][] => {
  return Object.entries(o) as [keyof T, T[keyof T]][];
};

const getObjectPathsRecursively = (obj: AnyObject, prefix = ''): string[] =>
  objectKeys(obj).reduce((acc: string[], key) => {
    const value = obj[key];

    if (typeof value === 'object' && value !== null) {
      return [...acc, ...getObjectPathsRecursively(value as AnyObject, `${prefix}${String(key)}.`)];
    }

    return [...acc, `${prefix}${String(key)}`];
  }, []);

export const getObjectPaths = (source: any): string[] => getObjectPathsRecursively(source);

export const flattenObject = (input: Record<string | number | symbol, any>): Record<Path, string> => {
  const toReturn: Record<Path, any> = {};

  for (const property in input) {
    if (!input.hasOwnProperty(property)) {
      continue;
    }

    if (typeof input[property] == 'object' && input[property] !== null) {
      const flatObject = flattenObject(input[property]);
      for (const key in flatObject) {
        if (!flatObject.hasOwnProperty(key)) {
          continue;
        }

        toReturn[property + '.' + key] = flatObject[key];
      }
    } else {
      toReturn[property] = input[property];
    }
  }
  return toReturn;
};

const sortObject = <T>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map((item) => sortObject(item)) as T;
  }

  if (isPlainObject(obj) === false) {
    return obj;
  }

  return objectKeys(obj as object)
    .sort()
    .reduce((result, key) => {
      result[key] = sortObject(obj[key]);
      return result;
    }, {} as T);
};

const safeBufferMessage = async (message: string): Promise<ArrayBuffer> => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    // Browser environment
    const msgBuffer = new TextEncoder().encode(message);
    return await crypto.subtle.digest('SHA-256', msgBuffer);
  }

  if (typeof require === 'function') {
    const crypto = require('crypto');
    return crypto.createHash('sha256').update(message).digest();
  }

  throw new Error('Unsupported environment');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const hashObject = async <T extends Record<string, any>>(obj: T): Promise<string> => {
  const message = JSON.stringify(sortObject(obj));

  const hashBuffer = await safeBufferMessage(message);

  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Returns the maximum nesting depth of a JavaScript value.
 *
 * - Primitives (including undefined), null, and non-object values return 0.
 * - An empty or flat object/array has depth 1.
 * - Depth counts each level of nesting (objects and arrays).
 * - If a positive finite `limit` is provided, the function will short-circuit
 *   traversal as soon as any path reaches that depth and return the limit value.
 *
 * The implementation is fully iterative (explicit stack) to safely support
 * extremely deep objects without exceeding the JavaScript call stack limit.
 * Circular references are detected and do not cause infinite loops.
 */
export const getObjectDepth = (obj: unknown, limit?: number): number => {
  if (obj === null || typeof obj !== 'object') {
    return 0;
  }

  const hasLimit = typeof limit === 'number' && Number.isFinite(limit) && limit > 0;
  const effectiveLimit = hasLimit ? limit : Infinity;

  let maxDepth = 0;
  // Use stack of enter/leave actions to simulate recursion and manage current path for cycle detection.
  // This allows counting the deepest path to shared objects (per "serialized path" semantics)
  // while preventing infinite loops on cycles.
  const stack: Array<{ value: object; level: number; action: 'enter' | 'leave' }> = [];
  const path = new WeakSet();

  stack.push({ value: obj, level: 1, action: 'enter' });

  while (stack.length > 0) {
    const entry = stack.pop()!;
    const { value, level, action } = entry;

    if (action === 'leave') {
      path.delete(value);
      continue;
    }

    // enter action
    if (path.has(value)) {
      // cycle / back edge in current path: do not descend or count extra
      continue;
    }

    if (level > maxDepth) {
      maxDepth = level;
      if (maxDepth >= effectiveLimit) {
        return limit!;
      }
    }

    path.add(value);
    stack.push({ value: value, level: level, action: 'leave' });

    const nextLevel = level + 1;
    if (nextLevel > effectiveLimit) {
      return limit!;
    }

    // Collect children and push in reverse so they are processed in original order.
    const children: object[] = [];
    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        const child = (value as Record<string, unknown>)[key];
        if (child !== null && typeof child === 'object') {
          children.push(child);
        }
      }
    }
    for (const child of children.slice().reverse()) {
      stack.push({ value: child, level: nextLevel, action: 'enter' });
    }
  }

  return maxDepth;
};
